/**
 * Справочник тегов.
 *
 * Свободный ввод тегов в игре убран: пользователь писал «города», и
 * англоязычный игрок видел ровно то же самое. Теперь у тега есть код, который
 * лежит в квестах, и названия на каждом языке — их и ведёт модератор.
 */
import { DeleteButton, List, useTable } from "@refinedev/antd";
import { useCreate, useUpdate, type HttpError } from "@refinedev/core";
import { Button, Card, Form, Input, Space, Table, Typography, message } from "antd";

interface Tag {
  id: string;
  slug?: string;
  nameEn?: string;
  nameRu?: string;
}

export function TagList() {
  const { tableProps, tableQuery } = useTable<Tag, HttpError>({
    resource: "tags",
    sorters: { mode: "off" },
    pagination: { pageSize: 100 },
  });

  const [createForm] = Form.useForm();
  const { mutate: createTag, mutation: creating } = useCreate();
  const { mutate: updateTag, mutation: updating } = useUpdate();

  const add = (values: { slug: string; nameEn: string; nameRu: string }) => {
    createTag(
      { resource: "tags", values },
      {
        onSuccess: () => {
          message.success("Тег добавлен");
          createForm.resetFields();
          void tableQuery.refetch();
        },
        onError: (error) => message.error(error.message),
      },
    );
  };

  const rename = (tag: Tag, values: { nameEn: string; nameRu: string }) => {
    updateTag(
      { resource: "tags", id: tag.id, values },
      {
        onSuccess: () => message.success("Название обновлено"),
        onError: (error) => message.error(error.message),
      },
    );
  };

  return (
    <List title="Теги">
      <Card size="small" style={{ marginBottom: 16 }}>
        <Form form={createForm} layout="inline" onFinish={add}>
          <Form.Item
            name="slug"
            rules={[
              { required: true, message: "Код обязателен" },
              {
                pattern: /^[a-z0-9][a-z0-9-]{0,31}$/,
                // Код уходит в ссылки и в фильтр каталога: кириллица и
                // пробелы в нём пришлось бы кодировать в каждом URL.
                message: "Латиница, цифры и дефис",
              },
            ]}
          >
            <Input placeholder="Код, например cities" style={{ width: 200 }} />
          </Form.Item>

          <Form.Item name="nameEn" rules={[{ required: true, message: "Название обязательно" }]}>
            <Input placeholder="Название по-английски" style={{ width: 220 }} />
          </Form.Item>

          <Form.Item name="nameRu" rules={[{ required: true, message: "Название обязательно" }]}>
            <Input placeholder="Название по-русски" style={{ width: 220 }} />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={creating.isPending}>
              Добавить
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Table {...tableProps} rowKey="id">
        <Table.Column
          dataIndex="slug"
          title="Код"
          width={200}
          render={(slug: string) => <Typography.Text code>{slug}</Typography.Text>}
        />

        <Table.Column<Tag>
          title="Названия"
          render={(_, tag) => (
            <Form
              layout="inline"
              initialValues={{ nameEn: tag.nameEn, nameRu: tag.nameRu }}
              onFinish={(values) => rename(tag, values as { nameEn: string; nameRu: string })}
            >
              <Form.Item name="nameEn" rules={[{ required: true, message: "" }]}>
                <Input placeholder="English" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item name="nameRu" rules={[{ required: true, message: "" }]}>
                <Input placeholder="Русский" style={{ width: 200 }} />
              </Form.Item>
              <Form.Item>
                <Button htmlType="submit" loading={updating.isPending}>
                  Сохранить
                </Button>
              </Form.Item>
            </Form>
          )}
        />

        <Table.Column<Tag>
          title=""
          width={120}
          render={(_, tag) => (
            <Space>
              <DeleteButton
                hideText
                size="small"
                resource="tags"
                recordItemId={tag.id}
                // Удаление снимает тег со всех квестов: метка, которую нечем
                // показать, хуже её отсутствия.
                confirmTitle="Удалить тег и снять его со всех квестов?"
                onSuccess={() => void tableQuery.refetch()}
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
}
